import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Button } from './Button';
import { Panel } from './Panel';

/*
 * ── A foldable, re-orderable section shell ──────────────────────────────────
 *
 * The box is `Panel`'s — hairline border, `bg-surface`, `rounded-lg` — with a
 * header that folds its body and, optionally, two arrows that move the whole
 * section up or down a list. Built for the company detail page, where a record
 * is read as a stack of sections a person opens, closes and arranges; it has
 * nothing company-specific in it and is meant to be reused.
 *
 * ── THE BODY STAYS MOUNTED WHEN FOLDED ──────────────────────────────────────
 *
 * ⚠️ Collapsing sets the `hidden` attribute on the body; it NEVER unmounts the
 * children. The children are panels that fetch on mount and hold edit drafts
 * and "Saved." notices — unmounting on fold would refetch on every open (one
 * request per fold, against the shared rate budget) and discard a half-typed
 * value the moment somebody closed the section to look at another. The same
 * ruling `CompanyFormModal`'s own `Section` made: typing in a section and then
 * closing it must not throw the input away. `hidden` also keeps the content out
 * of the tab order and the accessibility tree, so nothing is reachable while
 * folded.
 *
 * ── DISCLOSURE SEMANTICS ────────────────────────────────────────────────────
 *
 * A button and a region, not `<details>`, so a caller can force a section open
 * (a failed save, a deep link) without fighting the element's own state. The
 * shape is `<h2><button aria-expanded aria-controls>…</button></h2>` — the
 * heading WRAPS the button rather than sitting inside it, because a `<button>`
 * may only contain phrasing content and an `<h2>` is not that. Same shape as
 * `CompanyFormModal`'s `Section` and the APG disclosure pattern.
 *
 * The whole left-hand area — chevron, title and summary — is the one click
 * target, so the fold is easy to hit. `actions` and the move controls are
 * SIBLINGS of that button, never children of it, which is what guarantees that
 * pressing an action does not also toggle the fold: no `stopPropagation`, no
 * event plumbing that a later edit can forget.
 *
 * The `<section>` is named by the TITLE alone (`aria-labelledby` points at the
 * title span, not the whole heading), so a summary reading "3 contacts" does
 * not become part of the landmark's name. The heading itself still carries the
 * summary, which is fine: a heading that says "Contacts 3 contacts" when read
 * by headings is informative rather than noisy.
 *
 * ── REORDERING: ARROWS ONLY IN THIS FIRST CUT ───────────────────────────────
 *
 * There is deliberately no drag handle yet. ⚠️ Native HTML5 drag does not work
 * on touch at all — the kanban boards and `/projects` both learned this — so
 * the arrows are the touch path AND the keyboard path and have to exist
 * whatever else does. Drag can be layered on later (the way
 * `ProjectOrderControls` does, with a grip that hides itself on a coarse
 * pointer) without changing these props: `onMove` already expresses the move,
 * and a drag would only be a second way to fire it.
 *
 * An arrow that cannot move (first or last) is DISABLED WITH A REASON, never a
 * bare `disabled`: `Button` routes a `title` on a disabled control to a styled
 * tooltip on a wrapper span (a native `title` would never show, because the
 * button carries `disabled:pointer-events-none`). The arrows name the section
 * in their `aria-label`, because a screen reader meeting six "Move up" buttons
 * down a page cannot otherwise tell them apart.
 *
 * ── WHAT IS NOT HERE, ON PURPOSE ────────────────────────────────────────────
 *
 * No description or helper line under the heading — the no-helper-text rule.
 * `summary` is one short line of FACTS (a count, a balance), muted, beside the
 * title, and it stays visible folded or open so a closed section still says
 * what is inside it. No padding on the body: the children bring their own,
 * exactly as `Panel` children do, so a table can sit flush and a form can
 * indent itself.
 */
export interface CollapsibleSectionProps {
  /**
   * Stable per page. Used to derive the heading, title and body ids that wire
   * `aria-labelledby` and `aria-controls` together, so two sections on one
   * page must not share one.
   */
  id: string;
  /** The `<h2>` text. */
  title: ReactNode;
  /**
   * Plain-text name of the section for the move buttons' `aria-label`s
   * ("Move Contacts up"). Needed only when `title` is not a plain string —
   * when it is, the string itself is used. When neither is available the
   * labels fall back to "this section", which is announced rather than
   * nothing but tells six identical arrows apart no better than nothing does,
   * so pass one.
   */
  titleText?: string;
  /**
   * One short line of FACTS (counts, a balance) shown muted beside the title,
   * visible whether folded or open. Never prose — that is helper text.
   */
  summary?: ReactNode;
  /**
   * Right-aligned controls — an "Add contact" button, a "+" menu. Rendered
   * outside the disclosure button, so clicking one never toggles the fold.
   */
  actions?: ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  /** When absent, no move controls render at all. */
  onMove?: (direction: 'up' | 'down') => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  className?: string;
  children: ReactNode;
}

const FIRST_REASON = 'Already first.';
const LAST_REASON = 'Already last.';
const UNNAMED = 'this section';

export function CollapsibleSection({
  id,
  title,
  titleText,
  summary,
  actions,
  collapsed,
  onToggle,
  onMove,
  canMoveUp = true,
  canMoveDown = true,
  className,
  children,
}: CollapsibleSectionProps) {
  const headingId = `${id}-heading`;
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const open = !collapsed;

  // Only a string title can label a button on its own; anything else needs
  // `titleText`, or the label degrades to a generic noun rather than to
  // "[object Object]" or an empty string.
  const name = titleText ?? (typeof title === 'string' ? title : UNNAMED);

  return (
    <Panel aria-labelledby={titleId} className={className}>
      {/*
       * The header's `px-4 py-3` edge (Panel's own) is reached as row padding
       * plus button padding, so the disclosure button's hover surface is inset
       * from the panel edge the way every other ghost control here is, while
       * its hit area still runs the full width of the left-hand side.
       */}
      {/*
       * `flex-wrap` plus a floor on the heading: at 320px a title beside an
       * "Add contact" button and two move arrows was squeezed to "C…", so the
       * controls now wrap under the title (pushed right by `ml-auto`) once the
       * heading cannot keep 10rem. Measured, not reasoned — see the company
       * page's 320 run.
       */}
      <div className="flex flex-wrap items-center gap-1 py-1 pl-2 pr-3">
        <h2 id={headingId} className="min-w-[10rem] flex-1 text-sm font-semibold text-fg">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={bodyId}
            className={cx(
              'flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              'hover:bg-surface-hover active:bg-surface-active',
            )}
          >
            <ChevronIcon open={open} />
            {/*
             * Title and summary sit on one baseline and the summary wraps
             * under the title where the row runs out of room (320px), rather
             * than being hidden below `sm`: the brief is that a folded section
             * still says what it holds, at every width.
             */}
            <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span id={titleId} className="truncate">
                {title}
              </span>
              {summary !== undefined && summary !== null && summary !== '' && (
                <span className="truncate text-xs font-normal text-fg-muted">{summary}</span>
              )}
            </span>
          </button>
        </h2>

        <div className="ml-auto flex shrink-0 items-center gap-2">
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}

        {onMove && (
          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              leadingIcon={<ArrowUpIcon />}
              disabled={!canMoveUp}
              onClick={() => onMove('up')}
              aria-label={`Move ${name} up`}
              {...(canMoveUp ? {} : { title: FIRST_REASON })}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              leadingIcon={<ArrowDownIcon />}
              disabled={!canMoveDown}
              onClick={() => onMove('down')}
              aria-label={`Move ${name} down`}
              {...(canMoveDown ? {} : { title: LAST_REASON })}
            />
          </div>
        )}
        </div>
      </div>

      {/*
       * The rule between header and body lives on the BODY, so a folded section
       * shows one hairline (the panel's) and not a second one trapped inside
       * it. `hidden`, not a conditional render — see the header comment.
       */}
      <div id={bodyId} hidden={collapsed} className="border-t border-border">
        {children}
      </div>
    </Panel>
  );
}

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/*
 * One path, rotated, rather than two paths swapped: the rotation is what
 * animates, and `aria-expanded` on the button is the real state — the chevron
 * is decoration (`aria-hidden`) and colour is never the only signal.
 */
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      {...stroke}
      className={cx(
        'h-4 w-4 shrink-0 text-fg-subtle',
        'transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-90',
      )}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}
